import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { auth } from '../firebase';
import { BrandLogo, useBranding } from '../contexts/BrandingContext';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

type Status = 'verifying' | 'ready' | 'invalid' | 'done';

export default function ResetPassword() {
  const { branding } = useBranding();
  const [params] = useSearchParams();
  const oobCode = params.get('oobCode') || '';
  const navigate = useNavigate();

  const [status, setStatus] = useState<Status>('verifying');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!oobCode) { setStatus('invalid'); return; }
    verifyPasswordResetCode(auth, oobCode)
      .then((em) => { setEmail(em); setStatus('ready'); })
      .catch(() => setStatus('invalid'));
  }, [oobCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password should be at least 6 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setStatus('done');
      setTimeout(() => navigate('/login'), 2500);
    } catch {
      setError('This reset link has expired or was already used. Please request a new one from the sign-in page.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      className="flex min-h-screen items-center justify-center bg-[#F7F9FB] px-6 py-14 text-[#0B1C30]"
      style={{
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        backgroundImage:
          'radial-gradient(circle at 88% 8%, rgba(0,137,137,.16), transparent 30%), radial-gradient(circle at 10% 92%, rgba(255,185,92,.12), transparent 26%)',
      }}
    >
      <section className="w-full max-w-[520px] rounded-[22px] border border-[#DCE2E5] bg-white px-8 py-10 shadow-[0_18px_48px_rgba(15,42,50,0.12)] sm:px-12 sm:py-12">
        <div className="mb-12 flex items-center gap-4">
          <BrandLogo className="h-[52px] w-[52px] rounded-[13px] object-contain" />
          <div>
            <div className="text-[21px] font-bold leading-tight text-[#001820]">{branding.productName}</div>
            <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#5F696C]">
              Study Build Automation
            </div>
          </div>
        </div>

        <div>
          {status === 'verifying' && (
            <div>
              <p className="mb-3 text-sm font-bold uppercase tracking-[0.14em] text-[var(--brand-primary)]">Account security</p>
              <h1 className="text-4xl font-normal tracking-[-0.04em] text-[#001820]">Reset your password</h1>
              <p className="mt-4 text-[15px] leading-6 text-[#5F696C]">Checking your reset link…</p>
            </div>
          )}

          {status === 'invalid' && (
            <div>
              <p className="mb-3 text-sm font-bold uppercase tracking-[0.14em] text-[var(--brand-primary)]">Account security</p>
              <h1 className="mb-7 text-4xl font-normal tracking-[-0.04em] text-[#001820]">Reset your password</h1>
              <div className="mb-6 flex items-center space-x-2 rounded-[13px] border border-red-200 bg-red-50 p-4 text-red-700">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm text-left">This reset link is invalid, expired, or already used.</span>
              </div>
              <Link to="/login" className="inline-flex w-full justify-center rounded-full bg-[#001820] px-[22px] py-3.5 text-[15px] font-bold text-white shadow-[0_8px_20px_rgba(0,24,32,0.16)] transition hover:bg-[#00313A]">
                Back to sign in
              </Link>
            </div>
          )}

          {status === 'done' && (
            <div>
              <p className="mb-3 text-sm font-bold uppercase tracking-[0.14em] text-[var(--brand-primary)]">Account security</p>
              <h1 className="mb-7 text-4xl font-normal tracking-[-0.04em] text-[#001820]">Password updated</h1>
              <div className="mb-6 flex items-center space-x-2 rounded-[13px] border border-emerald-200 bg-emerald-50 p-4 text-emerald-800">
                <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm text-left">Your password has been reset. Redirecting to sign in…</span>
              </div>
              <Link to="/login" className="inline-flex w-full justify-center rounded-full bg-[#001820] px-[22px] py-3.5 text-[15px] font-bold text-white shadow-[0_8px_20px_rgba(0,24,32,0.16)] transition hover:bg-[#00313A]">
                Go to sign in now
              </Link>
            </div>
          )}

          {status === 'ready' && (
            <>
              <p className="mb-3 text-sm font-bold uppercase tracking-[0.14em] text-[var(--brand-primary)]">Account security</p>
              <h1 className="text-4xl font-normal tracking-[-0.04em] text-[#001820]">Choose a new password</h1>
              <p className="mb-8 mt-4 text-[15px] leading-6 text-[#5F696C]">For {email}</p>

              {error && (
                <div className="mb-5 flex items-center space-x-2 rounded-[13px] border border-red-200 bg-red-50 p-4 text-red-700">
                  <AlertCircle className="h-5 w-5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="mb-2 block text-sm font-bold text-[#0B1C30]" htmlFor="new-password">New password</label>
                    <input
                      id="new-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-14 w-full rounded-[13px] border border-[#CAD2D5] px-4 text-base outline-none transition focus:border-[var(--brand-primary)] focus:ring-4 focus:ring-[var(--brand-primary)]/15"
                      placeholder="••••••••"
                      autoComplete="new-password"
                      required
                      minLength={6}
                    />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-bold text-[#0B1C30]" htmlFor="confirm-password">Confirm new password</label>
                    <input
                      id="confirm-password"
                      type="password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      className="h-14 w-full rounded-[13px] border border-[#CAD2D5] px-4 text-base outline-none transition focus:border-[var(--brand-primary)] focus:ring-4 focus:ring-[var(--brand-primary)]/15"
                      placeholder="••••••••"
                      autoComplete="new-password"
                      required
                      minLength={6}
                    />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full bg-[#001820] px-[22px] py-3.5 text-[15px] font-bold text-white shadow-[0_8px_20px_rgba(0,24,32,0.16)] transition hover:bg-[#00313A] focus:ring-4 focus:ring-[var(--brand-primary)]/20 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? 'Please wait…' : 'Reset password'}
                </button>
              </form>
            </>
          )}
        </div>
      </section>
    </main>
  );
}
